import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  CalendarDays, 
  Clock, 
  Repeat,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  Pause,
  Play,
  Edit2,
  Calendar,
  DollarSign,
  User,
  Scissors,
  X
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Every Week", weeks: 1 },
  { value: "biweekly", label: "Every 2 Weeks", weeks: 2 },
  { value: "monthly", label: "Every Month", weeks: 4 }
];

const DAYS_OF_WEEK = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" }
];

const RecurringAppointments = ({ token, stylists = [], services = [] }) => {
  const [recurringAppointments, setRecurringAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  const [formData, setFormData] = useState({
    stylist_id: "",
    service_id: "",
    frequency: "biweekly",
    preferred_day: "Saturday",
    preferred_time: "10:00",
    notes: ""
  });

  // Get services for selected stylist
  const [stylistServices, setStylistServices] = useState([]);

  useEffect(() => {
    fetchRecurringAppointments();
  }, [token]);

  useEffect(() => {
    if (formData.stylist_id) {
      fetchStylistServices(formData.stylist_id);
    } else {
      setStylistServices(services);
    }
  }, [formData.stylist_id, services]);

  const fetchStylistServices = async (stylistId) => {
    try {
      const response = await axios.get(`${API}/services/stylist/${stylistId}`);
      setStylistServices(response.data.length > 0 ? response.data : services);
    } catch (error) {
      setStylistServices(services);
    }
  };

  const fetchRecurringAppointments = async () => {
    try {
      const response = await axios.get(`${API}/recurring/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecurringAppointments(response.data);
    } catch (error) {
      console.error("Failed to fetch recurring appointments:", error);
      toast.error("Failed to load standing appointments");
    } finally {
      setLoading(false);
    }
  };

  const createRecurringAppointment = async () => {
    if (!formData.stylist_id || !formData.service_id) {
      toast.error("Please select a stylist and service");
      return;
    }

    setActionLoading("create");
    try {
      const response = await axios.post(`${API}/recurring/`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Standing appointment created!");
      resetForm();
      fetchRecurringAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create standing appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const updateRecurringAppointment = async () => {
    if (!editingId) return;

    setActionLoading("update");
    try {
      const updateData = {
        frequency: formData.frequency,
        preferred_day: formData.preferred_day,
        preferred_time: formData.preferred_time,
        notes: formData.notes
      };
      
      const response = await axios.put(`${API}/recurring/${editingId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Standing appointment updated!");
      resetForm();
      fetchRecurringAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const cancelRecurringAppointment = async (recurringId) => {
    if (!confirm("Are you sure you want to cancel this standing appointment?")) return;
    
    setActionLoading(recurringId);
    try {
      await axios.delete(`${API}/recurring/${recurringId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Standing appointment cancelled");
      fetchRecurringAppointments();
    } catch (error) {
      toast.error("Failed to cancel appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const pauseAppointment = async (recurringId) => {
    setActionLoading(recurringId);
    try {
      await axios.post(`${API}/recurring/${recurringId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Standing appointment paused");
      fetchRecurringAppointments();
    } catch (error) {
      toast.error("Failed to pause appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const resumeAppointment = async (recurringId) => {
    setActionLoading(recurringId);
    try {
      const response = await axios.post(`${API}/recurring/${recurringId}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Appointment resumed! Next: ${response.data.next_appointment_date}`);
      fetchRecurringAppointments();
    } catch (error) {
      toast.error("Failed to resume appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const bookNextAppointment = async (recurringId) => {
    setActionLoading(`book-${recurringId}`);
    try {
      const response = await axios.post(`${API}/recurring/${recurringId}/book-next`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchRecurringAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setActionLoading(null);
    }
  };

  const startEditing = (appointment) => {
    setFormData({
      stylist_id: appointment.stylist_id,
      service_id: appointment.service_id,
      frequency: appointment.frequency || "biweekly",
      preferred_day: appointment.preferred_day,
      preferred_time: appointment.preferred_time,
      notes: appointment.notes || ""
    });
    setEditingId(appointment.recurring_id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      stylist_id: "",
      service_id: "",
      frequency: "biweekly",
      preferred_day: "Saturday",
      preferred_time: "10:00",
      notes: ""
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const getFrequencyLabel = (frequency) => {
    return FREQUENCY_OPTIONS.find(f => f.value === frequency)?.label || frequency;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-700 border-green-200";
      case "paused": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "cancelled": return "bg-red-100 text-red-700 border-red-200";
      default: return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not scheduled";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Standing Appointments</h2>
          <p className="text-stone-400">Set up recurring appointments that auto-repeat</p>
        </div>
        {!showAddForm && (
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold hover:from-amber-400 hover:to-orange-400"
            data-testid="new-standing-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Standing Appointment
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-amber-500/30 bg-stone-900/50 backdrop-blur">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Repeat className="w-5 h-5 text-amber-500" />
                {editingId ? "Edit Standing Appointment" : "Create Standing Appointment"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Stylist Selection */}
              <div>
                <Label className="text-stone-300">Stylist / Barber</Label>
                <Select 
                  value={formData.stylist_id} 
                  onValueChange={(value) => setFormData({...formData, stylist_id: value, service_id: ""})}
                  disabled={!!editingId}
                >
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white" data-testid="stylist-select">
                    <SelectValue placeholder="Select a stylist" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {stylists.map((stylist) => (
                      <SelectItem 
                        key={stylist.user_id || stylist.user?.user_id} 
                        value={stylist.user_id || stylist.user?.user_id}
                        className="text-white hover:bg-stone-700"
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-amber-500" />
                          {stylist.user?.name || stylist.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Service Selection */}
              <div>
                <Label className="text-stone-300">Service</Label>
                <Select 
                  value={formData.service_id} 
                  onValueChange={(value) => setFormData({...formData, service_id: value})}
                  disabled={!!editingId || !formData.stylist_id}
                >
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white" data-testid="service-select">
                    <SelectValue placeholder={formData.stylist_id ? "Select a service" : "Select stylist first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {stylistServices.map((service) => (
                      <SelectItem 
                        key={service.service_id || service.id} 
                        value={service.service_id || service.id}
                        className="text-white hover:bg-stone-700"
                      >
                        <div className="flex items-center justify-between gap-4 w-full">
                          <span className="flex items-center gap-2">
                            <Scissors className="w-4 h-4 text-amber-500" />
                            {service.name}
                          </span>
                          <span className="text-amber-400">${(service.price || service.base_price || 0).toFixed(2)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Frequency */}
              <div>
                <Label className="text-stone-300">Frequency</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(value) => setFormData({...formData, frequency: value})}
                >
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white" data-testid="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} className="text-white hover:bg-stone-700">
                        <div className="flex items-center gap-2">
                          <Repeat className="w-4 h-4 text-amber-500" />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Preferred Day */}
              <div>
                <Label className="text-stone-300">Preferred Day</Label>
                <Select 
                  value={formData.preferred_day} 
                  onValueChange={(value) => setFormData({...formData, preferred_day: value})}
                >
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white" data-testid="day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value} className="text-white hover:bg-stone-700">
                        <div className="flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-amber-500" />
                          {day.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Preferred Time */}
              <div>
                <Label className="text-stone-300">Preferred Time</Label>
                <Input 
                  type="time" 
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                  className="mt-1 bg-stone-800 border-stone-700 text-white"
                  data-testid="time-input"
                />
              </div>
              
              {/* Notes */}
              <div className="md:col-span-2">
                <Label className="text-stone-300">Notes (Optional)</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special requests or preferences..."
                  className="mt-1 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 resize-none"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                onClick={editingId ? updateRecurringAppointment : createRecurringAppointment}
                disabled={actionLoading === "create" || actionLoading === "update"}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold hover:from-amber-400 hover:to-orange-400"
                data-testid="save-standing-appointment-btn"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {editingId ? "Update Appointment" : "Create Standing Appointment"}
              </Button>
              <Button 
                variant="outline" 
                onClick={resetForm}
                className="border-stone-600 text-stone-300 hover:bg-stone-800"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Recurring Appointments */}
      {recurringAppointments.length === 0 ? (
        <Card className="bg-stone-900/50 border-stone-800">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 text-stone-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-300 mb-2">No Standing Appointments</h3>
            <p className="text-stone-500 text-sm mb-4 max-w-md mx-auto">
              Set up a recurring appointment to never miss your regular cut or style. 
              Perfect for maintaining that fresh look!
            </p>
            {!showAddForm && (
              <Button 
                onClick={() => setShowAddForm(true)}
                variant="outline"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Standing Appointment
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="recurring-appointments-list">
          {recurringAppointments.map((recurring) => (
            <Card 
              key={recurring.recurring_id} 
              className={`bg-stone-900/50 border-stone-800 hover:border-amber-500/30 transition-all ${
                recurring.status === 'paused' ? 'opacity-75' : ''
              }`}
              data-testid={`recurring-card-${recurring.recurring_id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left: Appointment Info */}
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      recurring.status === 'paused' 
                        ? 'bg-yellow-500/20' 
                        : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
                    }`}>
                      <Repeat className={`w-6 h-6 ${
                        recurring.status === 'paused' ? 'text-yellow-500' : 'text-amber-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white">
                          {recurring.service_name || "Service"}
                        </h3>
                        <Badge className={`text-xs ${getStatusColor(recurring.status || 'active')}`}>
                          {recurring.status || 'active'}
                        </Badge>
                        {recurring.service_price > 0 && (
                          <Badge variant="outline" className="text-amber-400 border-amber-500/50">
                            <DollarSign className="w-3 h-3 mr-1" />
                            {recurring.service_price.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-400 mt-1">
                        with <span className="text-amber-400">{recurring.stylist_name || "Stylist"}</span>
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-stone-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-4 h-4 text-amber-500/70" />
                          {recurring.preferred_day}s
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-amber-500/70" />
                          {recurring.preferred_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Repeat className="w-4 h-4 text-amber-500/70" />
                          {getFrequencyLabel(recurring.frequency)}
                        </span>
                      </div>
                      {recurring.notes && (
                        <p className="text-xs text-stone-500 mt-2 italic">
                          Note: {recurring.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Right: Next Date & Actions */}
                  <div className="flex flex-col md:items-end gap-3">
                    {/* Next Appointment */}
                    {recurring.next_appointment_date && recurring.status !== 'cancelled' && (
                      <div className="text-right bg-stone-800/50 rounded-lg px-3 py-2">
                        <p className="text-xs text-stone-500">Next appointment</p>
                        <p className="font-medium text-amber-400">
                          {formatDate(recurring.next_appointment_date)}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {recurring.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                            onClick={() => bookNextAppointment(recurring.recurring_id)}
                            disabled={actionLoading === `book-${recurring.recurring_id}`}
                            data-testid={`book-next-btn-${recurring.recurring_id}`}
                          >
                            {actionLoading === `book-${recurring.recurring_id}` ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-400 mr-1" />
                            ) : (
                              <Calendar className="w-3 h-3 mr-1" />
                            )}
                            Book Next
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-stone-600 text-stone-300 hover:bg-stone-800"
                            onClick={() => startEditing(recurring)}
                            data-testid={`edit-btn-${recurring.recurring_id}`}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                            onClick={() => pauseAppointment(recurring.recurring_id)}
                            disabled={actionLoading === recurring.recurring_id}
                            data-testid={`pause-btn-${recurring.recurring_id}`}
                          >
                            <Pause className="w-3 h-3 mr-1" />
                            Pause
                          </Button>
                        </>
                      )}
                      
                      {recurring.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          onClick={() => resumeAppointment(recurring.recurring_id)}
                          disabled={actionLoading === recurring.recurring_id}
                          data-testid={`resume-btn-${recurring.recurring_id}`}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Resume
                        </Button>
                      )}
                      
                      {recurring.status !== 'cancelled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => cancelRecurringAppointment(recurring.recurring_id)}
                          disabled={actionLoading === recurring.recurring_id}
                          data-testid={`cancel-btn-${recurring.recurring_id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-2 text-amber-400">How Standing Appointments Work</p>
              <ul className="list-disc list-inside space-y-1 text-stone-400">
                <li>Your appointments are automatically scheduled based on your preferred frequency</li>
                <li>Click <span className="text-green-400">"Book Next"</span> to confirm and book your next appointment</li>
                <li>You'll receive reminders before each appointment</li>
                <li><span className="text-yellow-400">Pause</span> to temporarily stop without losing your settings</li>
                <li>If your preferred time is unavailable, we'll suggest alternatives</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecurringAppointments;
